import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell, StatusDot, TableSkeleton } from '@redpanda/ui';
import { formatTableDate, useRefetchOnFocus, useRememberedState } from '@redpanda/utils';
import { useMyTasksStore } from '../stores/pmTaskStore';
import ProjectLimitBanner from '../components/ProjectLimitBanner';
import { useDataChanged } from '../utils/dataEvents';
import { priorityTone, taskTone, toneOf } from '../utils/statusTone';
import { getTodayDateInput, isPastDue, toDateInput } from '../utils/dateUtils';

type QuickFilter = 'all' | 'today' | 'thisWeek' | 'overdue';
const FILTERS: QuickFilter[] = ['all', 'today', 'thisWeek', 'overdue'];

/** 本週最後一天（週六）的日期字串 */
const endOfWeekDate = () => {
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + (6 - now.getDay()));
  return `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`;
};

const MyTasksPage: React.FC = () => {
  const { t, i18n } = useTranslation(['translation', 'pages']);
  const { myTasks, isLoading, fetchMyTasks } = useMyTasksStore();
  const [quickFilter, setQuickFilter] = useRememberedState<QuickFilter>('pm-ui:myTasks.filter', 'all', FILTERS);

  useEffect(() => {
    fetchMyTasks();
  }, [fetchMyTasks]);
  useDataChanged('tasks', () => fetchMyTasks(true));
  useRefetchOnFocus(() => fetchMyTasks(true));

  // 篩選以「日期」比較（今天到期不算逾期；與儀表板、專案任務分頁一致）
  const today = getTodayDateInput();
  const weekEnd = endOfWeekDate();
  const filteredTasks = myTasks.filter(task => {
    if (quickFilter === 'all') return true;
    if (!task.dueDate || task.status === 'COMPLETED') return false;
    const due = toDateInput(task.dueDate);
    if (quickFilter === 'today') return due === today;
    if (quickFilter === 'thisWeek') return due >= today && due <= weekEnd;
    return due < today;
  });

  const count = (status: string) => myTasks.filter(task => task.status === status).length;

  const filterClass = (active: boolean) =>
    `inline-flex items-center rounded px-3 py-1.5 text-sm transition-colors ${
      active ? 'bg-accent-soft text-accent font-medium' : 'text-fg-secondary hover:text-fg'
    }`;

  return (
    <div className="container mx-auto px-10 py-6 pb-32">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{t('pmTasks.myTasks', { ns: 'pages' })}</h1>
      </div>

      <ProjectLimitBanner />

      {/* 快速篩選（分段切換，與其他頁搜尋列同一位置、同高）＋一行摘要（取代 4 張彩色統計卡） */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <nav aria-label={t('pmTasks.filter.label', { ns: 'pages' })} className="inline-flex h-10 items-center rounded-md border border-line bg-surface p-0.5">
          {FILTERS.map(filter => (
            <button key={filter} type="button" onClick={() => setQuickFilter(filter)} aria-pressed={quickFilter === filter} className={filterClass(quickFilter === filter)}>
              {t(`pmTasks.filter.${filter}`, { ns: 'pages' })}
            </button>
          ))}
        </nav>
        {myTasks.length > 0 && (
          <p className="text-[13px] text-fg-secondary tabular-nums">
            {t('pmTasks.stats.total', { ns: 'pages' })} {myTasks.length}
            <span className="mx-2 text-fg-muted">·</span>
            {t('pmTasks.stats.pending', { ns: 'pages' })} {count('PENDING')}
            <span className="mx-2 text-fg-muted">·</span>
            {t('pmTasks.stats.inProgress', { ns: 'pages' })} {count('IN_PROGRESS')}
            <span className="mx-2 text-fg-muted">·</span>
            {t('pmTasks.stats.completed', { ns: 'pages' })} {count('COMPLETED')}
          </p>
        )}
      </div>

      {isLoading && myTasks.length === 0 ? (
        <TableSkeleton rows={6} columns={6} showHeader={true} />
      ) : (
        <div className="bg-surface shadow dark:shadow-none dark:border dark:border-line overflow-hidden rounded-lg">
          <div className="overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            <Table style={{ tableLayout: 'fixed', minWidth: '800px' }}>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('pmTasks.fields.title', { ns: 'pages' })}</TableHead>
                  <TableHead className="w-48">{t('dashboard.project', { ns: 'pages' })}</TableHead>
                  <TableHead numeric className="w-28">{t('pmTasks.fields.dueDate', { ns: 'pages' })}</TableHead>
                  <TableHead className="w-24">{t('pmTasks.fields.priority', { ns: 'pages' })}</TableHead>
                  <TableHead className="w-28">{t('pmTasks.fields.status', { ns: 'pages' })}</TableHead>
                  <TableHead numeric className="w-48">{t('pmTasks.fields.hours', { ns: 'pages' })}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTasks.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="py-10 text-center whitespace-normal">
                      <p className="text-sm font-medium text-fg">
                        {quickFilter === 'all' ? t('pmTasks.noMyTasks', { ns: 'pages' }) : t('pmTasks.filter.noResults', { ns: 'pages' })}
                      </p>
                      {quickFilter === 'all' && (
                        <p className="mt-1 text-[13px] text-fg-secondary">{t('pmTasks.myTasksHint', { ns: 'pages' })}</p>
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredTasks.map(task => {
                    const overdue = task.status !== 'COMPLETED' && isPastDue(task.dueDate);
                    return (
                      <TableRow key={task.id}>
                        <TableCell className="whitespace-normal">
                          <div className="font-medium text-fg">{task.title}</div>
                          {task.description && <div className="mt-0.5 text-[13px] text-fg-secondary line-clamp-1">{task.description}</div>}
                        </TableCell>
                        <TableCell className="truncate">
                          {/* 任務沒有詳情頁：點專案名稱到該專案的任務分頁 */}
                          <Link to={`/projects/${task.projectId}?tab=tasks`} className="text-fg hover:text-accent hover:underline">
                            {task.project?.name || t('pmTasks.viewProject', { ns: 'pages' })}
                          </Link>
                        </TableCell>
                        <TableCell numeric>
                          {task.dueDate ? (
                            <span className={overdue ? 'text-red-600 dark:text-red-400' : ''}>
                              {formatTableDate(task.dueDate, i18n.language)}
                              {overdue && <span className="block text-[13px]">{t('pmTasks.overdue', { ns: 'pages' })}</span>}
                            </span>
                          ) : <span className="text-fg-muted">—</span>}
                        </TableCell>
                        <TableCell>
                          <StatusDot tone={toneOf(priorityTone, task.priority)} label={t(`pmTasks.priority.${task.priority}`, { ns: 'pages' })} />
                        </TableCell>
                        <TableCell>
                          <StatusDot tone={toneOf(taskTone, task.status)} label={t(`pmTasks.status.${task.status}`, { ns: 'pages' })} />
                        </TableCell>
                        <TableCell numeric>
                          {task.estimatedHours ? `${task.estimatedHours}h` : <span className="text-fg-muted">—</span>}
                          {task.actualHours ? <span className="text-fg-secondary"> / {task.actualHours}h</span> : null}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyTasksPage;
